import { generateOrthopedicBenchmarks } from '@/lib/payerSimulation';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { cn } from '@/lib/utils';
import { TrendingUp, Award, Sparkles } from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

export function OrthopedicBenchmarks() {
  const benchmarks = generateOrthopedicBenchmarks();
  
  // Calculate aggregate outperformance
  const avgVariance = benchmarks.reduce((sum, b) => sum + b.variance, 0) / benchmarks.length;

  return (
    <div className="glass-card-elevated p-4">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Award className="w-4 h-4 text-primary" />
          <h3 className="font-semibold text-sm">Orthopedic-Specific Benchmarks</h3>
        </div>
        <Badge className="bg-primary/20 text-primary border-primary/30">
          Beacon Orthopaedics
        </Badge>
      </div>

      {/* AI Insight Banner */}
      <div className="mb-4 p-3 rounded-lg bg-gradient-to-r from-primary/10 via-primary/5 to-transparent border border-primary/20">
        <div className="flex items-start gap-2">
          <Sparkles className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-sm font-medium text-primary">Insight</p>
            <p className="text-sm text-muted-foreground mt-1">
              Beacon is currently outperforming the national average by{' '}
              <span className="font-mono font-bold text-success">{avgVariance.toFixed(1)}%</span> due to 
              LINK's Predictive Denial Engine. High-value procedures show the strongest gains.
            </p>
          </div>
        </div>
      </div>

      {/* Peer Comparison Table */}
      <div className="mb-4">
        <h4 className="text-xs font-medium text-muted-foreground mb-3">
          Peer Comparison: Clean Claim Rate by Procedure
        </h4>
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <TableHead className="text-xs">CPT Code</TableHead>
              <TableHead className="text-xs">Procedure</TableHead>
              <TableHead className="text-xs text-center">Beacon Rate</TableHead>
              <TableHead className="text-xs text-center">National Avg</TableHead>
              <TableHead className="text-xs text-right">Advantage</TableHead>
              <TableHead className="text-xs text-right">Volume</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {benchmarks.map((benchmark) => (
              <TableRow key={benchmark.cptCode}>
                <TableCell className="font-mono font-medium text-sm">
                  {benchmark.cptCode}
                </TableCell>
                <TableCell className="text-sm text-muted-foreground max-w-[180px]">
                  {benchmark.description}
                </TableCell>
                <TableCell className="text-center">
                  <div className="flex flex-col items-center gap-1">
                    <span className="font-mono font-bold text-success text-sm">
                      {benchmark.beaconCleanClaimRate.toFixed(1)}%
                    </span>
                    <Progress 
                      value={benchmark.beaconCleanClaimRate} 
                      className="w-16 h-1.5"
                    />
                  </div>
                </TableCell>
                <TableCell className="text-center">
                  <div className="flex flex-col items-center gap-1">
                    <span className="font-mono text-muted-foreground text-sm">
                      {benchmark.nationalAverage.toFixed(1)}%
                    </span>
                    <Progress 
                      value={benchmark.nationalAverage} 
                      className="w-16 h-1.5 [&>div]:bg-muted-foreground"
                    />
                  </div>
                </TableCell>
                <TableCell className="text-right">
                  <Badge className={cn(
                    "font-mono",
                    benchmark.variance >= 10 
                      ? "bg-success/20 text-success border-success/30"
                      : "bg-primary/20 text-primary border-primary/30"
                  )}>
                    <TrendingUp className="w-3 h-3 mr-1" />
                    +{benchmark.variance.toFixed(1)}%
                  </Badge>
                </TableCell>
                <TableCell className="text-right font-mono text-sm text-muted-foreground">
                  {benchmark.claimVolume}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-3 gap-4 pt-4 border-t border-border/50">
        <div className="text-center">
          <p className="text-2xl font-bold font-mono text-success">97.4%</p>
          <p className="text-xs text-muted-foreground">Avg Clean Claim Rate</p>
        </div>
        <div className="text-center">
          <p className="text-2xl font-bold font-mono text-primary">+{avgVariance.toFixed(1)}%</p>
          <p className="text-xs text-muted-foreground">vs National Average</p>
        </div>
        <div className="text-center">
          <p className="text-2xl font-bold font-mono">1,138</p>
          <p className="text-xs text-muted-foreground">Total Claims (30d)</p>
        </div>
      </div>
    </div>
  );
}
